import React, { useState } from "react";
import { formatTimeAgo } from "views_shared/app/javascript/utils/dateFormatters";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

function AssignmentBanner({ request, onActionComplete }) {
  const [actionLoading, setActionLoading] = useState(null);
  const [assignConflict, setAssignConflict] = useState(null);
  const [error, setError] = useState(null);

  const currentUserId = globalThis.Qwoted?.CURRENT_USER_ID;

  const effectiveAssignedToId = assignConflict?.assigned_to_id || request.assigned_to_id;
  const effectiveAssignedToName = assignConflict?.assigned_to_name || request.assigned_to_name;
  const effectiveAssignedAt = assignConflict?.assigned_at || request.assigned_at;
  const isAssignedToMe = effectiveAssignedToId && effectiveAssignedToId === currentUserId;

  const handleAssign = async (force = false) => {
    setActionLoading("assign");
    setError(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const url = force
        ? `/admin/api/requests/${request.id}/assign?force=true`
        : `/admin/api/requests/${request.id}/assign`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });
      if (res.ok) {
        setAssignConflict(null);
        onActionComplete();
      } else if (res.status === 409) {
        const data = await res.json();
        setAssignConflict(data);
      } else {
        setError("Failed to assign request");
      }
    } catch {
      setError("Failed to assign request");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnassign = async () => {
    setActionLoading("unassign");
    setError(null);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(`/admin/api/requests/${request.id}/assign`, {
        method: "DELETE",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });
      if (res.ok || res.status === 204) {
        setAssignConflict(null);
        onActionComplete();
      } else {
        setError("Failed to unassign request");
      }
    } catch {
      setError("Failed to unassign request");
    } finally {
      setActionLoading(null);
    }
  };

  // Unassigned
  if (!effectiveAssignedToId) {
    return (
      <div className="alert alert-light border d-flex justify-content-between align-items-center mb-4 py-2">
        <span className="text-muted">
          <i className="fa fa-user-plus me-2" />
          Unassigned
        </span>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => handleAssign(false)}
          disabled={!!actionLoading}
        >
          {actionLoading === "assign" ? "Assigning..." : "Assign to me"}
        </button>
      </div>
    );
  }

  // Assigned to me
  if (isAssignedToMe) {
    return (
      <div className="alert alert-info d-flex justify-content-between align-items-center mb-4 py-2">
        <span>
          <i className="fa fa-user-check me-2" />
          <strong>Assigned to you</strong>
          {effectiveAssignedAt && <span className="text-muted ms-2">({formatTimeAgo(effectiveAssignedAt)})</span>}
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleUnassign}
          disabled={!!actionLoading}
        >
          {actionLoading === "unassign" ? "Unassigning..." : "Unassign"}
        </button>
      </div>
    );
  }

  // Assigned to someone else
  return (
    <div className="mb-4">
      <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0 py-2">
        <span>
          <i className="fa fa-user me-2" />
          <strong>Assigned to {effectiveAssignedToName}</strong>
          {effectiveAssignedAt && <span className="text-muted ms-2">({formatTimeAgo(effectiveAssignedAt)})</span>}
        </span>
        <button
          type="button"
          className="btn btn-outline-warning btn-sm"
          onClick={() => handleAssign(true)}
          disabled={!!actionLoading}
        >
          {actionLoading === "assign" ? "Overriding..." : "Override"}
        </button>
      </div>
      {assignConflict && (
        <div className="alert alert-info small mt-2 mb-0 py-2">
          <i className="fa fa-info-circle me-1" />
          Already assigned to {assignConflict.assigned_to_name}
          {assignConflict.assigned_at && <span> ({formatTimeAgo(assignConflict.assigned_at)})</span>}. Use{" "}
          <strong>Override</strong> to reassign.
        </div>
      )}
      {error && (
        <div className="alert alert-danger small mt-2 mb-0 py-2">
          <i className="fa fa-exclamation-circle me-1" />
          {error}
        </div>
      )}
    </div>
  );
}

export default AssignmentBanner;
